import React, { useEffect, useRef } from 'react';

interface PixelWordmarkProps {
  text: string;
  className?: string;
}

const ROWS = 18;
const FLICKER_STEP_MS = 460;
const REVEAL_MS = 1600;

// deterministic hash → [0,1), stable across frames for a given seed
const rnd = (n: number) => {
  const x = Math.sin(n) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Giant mosaic wordmark: the text is sampled into a grid of squares over a
 * faint full-bleed cell field. Letter cells assemble in random order when
 * scrolled into view, then shimmer forever (stepped per-cell flicker).
 */
const PixelWordmark: React.FC<PixelWordmarkProps> = ({ text, className = '' }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let revealStart = 0; // 0 = not yet revealed
    let visible = false;
    let cols = 0;
    let lit: boolean[] = []; // letter cells
    let cell = 0;
    let disposed = false;

    // Sample the wordmark at 1px-per-cell resolution
    const sample = () => {
      const off = document.createElement('canvas');
      const octx = off.getContext('2d');
      if (!octx) return;
      const fontPx = ROWS * 0.88;
      const tracking = `${(-fontPx * 0.07).toFixed(2)}px`; // tight, GIF-style letter fit
      octx.font = `800 ${fontPx}px Figtree, sans-serif`;
      octx.letterSpacing = tracking;
      const w = Math.ceil(octx.measureText(text).width) + 2;
      off.width = w;
      off.height = ROWS;
      octx.font = `800 ${fontPx}px Figtree, sans-serif`;
      octx.letterSpacing = tracking;
      octx.textBaseline = 'middle';
      octx.fillStyle = '#fff';
      octx.fillText(text, 1, ROWS / 2 + fontPx * 0.04);
      const data = octx.getImageData(0, 0, w, ROWS).data;
      cols = w;
      lit = new Array(w * ROWS);
      for (let i = 0; i < w * ROWS; i++) lit[i] = data[i * 4 + 3] > 110;
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = wrap.clientWidth;
      cell = width / cols;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(cell * ROWS * dpr);
      canvas.style.height = `${cell * ROWS}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now: number) => {
      const p = reduced
        ? 1
        : revealStart === 0
          ? 0
          : Math.min(1, (now - revealStart) / REVEAL_MS);
      const size = cell * 0.84;
      const pad = (cell - size) / 2;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c;
          const on = lit[i] && rnd(i * 7.31) <= p;
          let fill: string;
          if (on) {
            // quiet monochrome shimmer: narrow alpha band, slow stepped drift
            const step = Math.floor(now / FLICKER_STEP_MS + rnd(i * 1.7) * 10);
            const a = reduced ? 0.28 : 0.16 + 0.2 * rnd(i * 91.7 + step * 13.3);
            fill = `rgba(245,243,238,${a})`;
          } else {
            // grid stays visible everywhere, GIF-style
            fill = 'rgba(245,243,238,0.05)';
          }
          ctx.fillStyle = fill;
          ctx.fillRect(c * cell + pad, r * cell + pad, size, size);
        }
      }
    };

    const loop = (now: number) => {
      draw(now);
      if (visible && !reduced) raf = requestAnimationFrame(loop);
    };

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) {
        if (revealStart === 0) revealStart = performance.now();
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(loop);
      } else {
        cancelAnimationFrame(raf);
      }
    });

    const ro = new ResizeObserver(() => {
      if (!cols) return;
      resize();
      draw(performance.now());
    });

    document.fonts.load('800 16px Figtree').then(() => {
      if (disposed) return;
      sample();
      resize();
      draw(performance.now());
      io.observe(wrap);
      ro.observe(wrap);
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
    };
  }, [text]);

  return (
    <div ref={wrapRef} className={className}>
      <canvas ref={canvasRef} aria-hidden='true' className='block w-full' />
      <span className='sr-only'>{text}</span>
    </div>
  );
};

export default PixelWordmark;
